import React, { createContext, useContext, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { buildApiUrl, getAuthHeaders } from '../api/config'

// Create a context for authentication
const AuthContext = createContext()

// Provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [token, setToken] = useState(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  // Check for existing session on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('token')
    const storedUser = localStorage.getItem('user')

    const bootstrapSession = async () => {
      if (!storedToken) {
        setLoading(false)
        return
      }

      setToken(storedToken)

      // Use server-authoritative profile data to prevent stale/localStorage role mismatch.
      try {
        const response = await axios.get(buildApiUrl('account/profile/'), {
          headers: getAuthHeaders(storedToken),
        })
        const profile = response.data || null
        setUser(profile)
        localStorage.setItem('user', JSON.stringify(profile))
      } catch (error) {
        console.error('Session bootstrap failed, clearing auth state:', error)
        setUser(null)
        setToken(null)
        localStorage.removeItem('token')
        localStorage.removeItem('user')
      } finally {
        setLoading(false)
      }
    }

    // Keep legacy fallback parsing only as a temporary display bridge during bootstrap.
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser))
      } catch (error) {
        console.error('Error parsing stored user data:', error)
      }
    }
    bootstrapSession()
  }, [])

  // Login function
  const login = (userData, authToken) => {
    setUser(userData)
    setToken(authToken)
    localStorage.setItem('token', authToken)
    localStorage.setItem('user', JSON.stringify(userData))
  }

  // Logout function
  const logout = () => {
    setUser(null)
    setToken(null)
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    navigate('/login')
  }

  // Check if user is authenticated
  const isAuthenticated = !!token

  // Context value
  const value = {
    user,
    token,
    isAuthenticated,
    loading,
    login,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Custom hook for using auth context
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext 
